import { get } from "api/client";
import type { PlatformId } from "util/platform";
import type { SemVer } from "util/version";

export interface CrashEntry {
  title: string;
  subtitle: string;
  crashlytics_id: string;
  url: string;
  error: string;
  occurrences: number;
  devices: number;
  first_seen: SemVer;
  source: "firebase" | "sentry";
}

export interface ReleaseHistoryEntry {
  appBuildUrl?: string;
  version: SemVer;
  status: string;
  timestamp1Percent?: number;
  timestamp2Percent?: number;
  timestamp10Percent?: number;
  timestamp99Percent?: number;
  timestampComplete?: number;
  timestampKickoff?: number;
  qaManagerName?: string;
  releaseManagerName?: string;
}

export interface CrashesData {
  data: CrashEntry[];
}

export interface ChangelogEntry {
  teams: string[];
  author?: string;
  title: string;
  prReference?: {
    number: number;
    url: string;
  };
  jiraTickets: {
    key: string;
    url: string;
  }[];
  experiments: {
    name: string;
    url: string;
  }[];
  tags: {
    extraClassNames: string[];
    text: string;
  }[];
  appVersion?: SemVer;
  appBuildUrl?: string;
  commitUrl?: string;
  timestamp?: string;
}

export interface ChangelogData {
  entries: ChangelogEntry[];
}

export interface ReleaseHistoryData {
  entries: ReleaseHistoryEntry[];
}

export interface EventLogEntry {
  fromState: string | null;
  fromVersion?: string | null;
  timestamp: number;
  toState: string;
  toVersion?: string | null;
}

interface ObservationSummaryStats {
  independentVariableName: string;
  independentVariableValue: number;
  mean: number;
  median: number;
  p25: number;
  p75: number;
  stdDev: number;
}

interface MetricReport {
  baseline?: {
    previous?: {
      version: SemVer;
      metricValues: TimestampedObservation[];
    };
    aggregate?: {
      includedReleases: SemVer[];
      summaryStatValues: ObservationSummaryStats[];
    };
  };
  metricName: string;
  metricValues: TimestampedObservation[];
  platform: string;
  version: SemVer;
}

interface Observation {
  current_dau: number | string;
  readout: number | string;
  version: string;
}

export interface PlatformDataResponse {
  openPatchCount: number;
  platform: {
    id: PlatformId;
    name: string;
    repo: string;
  };
  releases: Release[];
}

export interface ReleaseManagers {
  managers: {
    name: string;
    scheduleId: string;
    scheduleName: string;
  }[];
}

export interface ReadinessItem {
  description: number;
  formattedValue: string;
  label: string;
  type: string;
  url?: string;
  /**
   * URL for the user to follow to create a metric investigation.
   *
   * If undefined, the readiness checklist is not showing a column for metric investigation links.
   *
   * If null, the readiness checklist is showing a column for metric investigation links but this
   * item does not represent a metric that supports investigation. For example:
   *  * This item is for QA approval (not a metric).
   *  * This item is for Session End p75 latency (a metric that does not support investigation).
   */
  createInvestigationUrl?: string | null;
  valid: boolean;
  shouldShowInChecklist?: boolean;
  shouldShowInSummary?: boolean;
}

export interface Release {
  build?: number | null;
  color: string;
  majorMinor: string;
  patch: number;
  readiness?: {
    releaseBlockers: ReadinessItem;
    metrics: ReadinessItem[];
    signoff: ReadinessItem;
  };
  readinessStatus: "go" | "stop" | "wait" | null;
  releaseEventLog?: EventLogEntry[];
  status: string;
  versionSuffix: string;
  // Dictionary mapping from version string to build URL if it exists.
  appBuildUrls: Record<string, string | undefined>;
}

export interface TimestampedObservation {
  timestamp: string;
  value: Observation;
}

export interface AppStoreReleaseStatus {
  app_store: string;
  status: string;
  color: string;
}

export type PerAppStoreReleaseStatus = AppStoreReleaseStatus[];

/**
 * Fetches the platform data from the server.
 *
 * @param params The query params to forward to the server.
 * @returns A list of platform `Data` objects.
 */
export const getAllPlatformData = async (params: URLSearchParams) =>
  Promise.all(
    ["android", "china", "ios", "web"].map(platform =>
      get<PlatformDataResponse>(`/platforms/${platform}?${params.toString()}`),
    ),
  );

/**
 * Fetches the release manager data from the server.
 *
 * @param platformId The platform ID to fetch the release managers for.
 * @param params The query params to forward to the server.
 * @returns A `ReleaseManagers` object.
 */
export const getReleaseManagerData = async (
  platformId: PlatformId,
  params: URLSearchParams,
): Promise<ReleaseManagers> =>
  get(`/platforms/${platformId}/releaseManagers?${params.toString()}`);

/**
 * Fetches the changelog for the given platform and versions.
 *
 * @param platformId The platform ID to fetch the release status for.
 * @param fromVersion The version to fetch the changelog from. If null, only fetches changelog for the toVersion.
 * @param toVersion The version to fetch the changelog to.
 * @returns A `ChangelogData` object
 */
export const getChangelog = async (
  platformId: PlatformId,
  fromVersion: string,
  toVersion: string,
): Promise<ChangelogData> =>
  get(`/changelog/${platformId}/${toVersion}/${fromVersion}`);

export const getReleaseHistory = async (
  platformId: PlatformId,
  fromVersion: string,
  toVersion: string,
): Promise<ReleaseHistoryData> =>
  get(`/release-history/${platformId}/${toVersion}/${fromVersion}`);

/**
 * Fetches the release data per version from the server.
 *
 * @param platformId The platform ID to fetch the release status for.
 * @param version The version to fetch the release status for.
 * @returns A `Release` object.
 */
export const getRelease = async (
  platformId: PlatformId,
  version: string,
): Promise<Release> => get(`/releases/${platformId}/${version}`);

/**
 * Fetches the crashes for the given release
 *
 * @param platformId The platform ID to fetch the crashes for.
 * @param version The version to fetch the changelog from.
 * @returns A `CrashesData` object
 */
export const getCrashes = async (
  platformId: PlatformId,
  version: string,
): Promise<CrashesData> => get(`/crashes/${platformId}/${version}`);

/**
 * Fetches the per-app store release status from the server.
 *
 * @param platformId The platform ID to fetch the release status for.
 * @param version The version to fetch the release status for.
 * @param params The query params to forward to the server.
 * @returns A list of `AppStoreReleaseStatus` objects.
 */
export const getPerAppStoreReleaseStatus = async (
  platformId: PlatformId,
  version: string,
  params: URLSearchParams,
): Promise<PerAppStoreReleaseStatus> =>
  get(
    `/releases/${platformId}/${version}/perAppStoreReleaseStatus?${params.toString()}`,
  );

/**
 * Given a version and platform ID, fetches all versions for that release. Typically the verison
 * will be the most recent release, and the function will return all previous versions for that release.
 *
 * @param platformId The platform ID to fetch the versions for.
 * @param version The version to fetch the versions for.
 * @returns A list of `SemVer` objects.
 */
export const getAllVersionsPerRelease = async (
  platformId: PlatformId,
  version: string,
): Promise<SemVer[]> => get(`/releases/${platformId}/${version}/allVersions`);

export const getMajorMinorVersions = async (
  platformId: PlatformId,
): Promise<SemVer[]> =>
  get(`/releases/${platformId}/allVersions?majorMinorOnly=true`);

export const getCrashesVersions = async (
  platformId: PlatformId,
): Promise<SemVer[]> =>
  get(`/releases/${platformId}/allVersions?lastPatchWithCrashData=true`);

/**
 * Fetches the metric report data from the server.
 *
 * Given a platform ID, version, and metric name, returns a timeseries of observations for the
 * metric. Additionally, returns the previous release's report as the baseline, if available.
 *
 * @param platformId The platform ID to fetch the report for.
 * @param version The version to fetch the report for (can include patch/build, but they will be discarded).
 * @param metricName The metric name to fetch the report for.
 * @returns A `ReportPair` object containing the current and baseline reports.
 */
export const getMetricReportData = async (
  platformId: string,
  version: string,
  metricName: string,
): Promise<MetricReport> =>
  get<MetricReport>(`/metric-reports/${platformId}/${version}/${metricName}`);

/**
 * Similar to `getMetricReportData`, but uses incomplete baseline data to speed up the response.
 * @param platformId The platform ID to fetch the report for.
 * @param version The version to fetch the report for.
 * @param metricName The metric name to fetch the report for.
 * @returns A `ReportPair` object containing the current and baseline reports.
 */
export const getMetricReportDataFast = async (
  platformId: string,
  version: string,
  metricName: string,
): Promise<MetricReport> =>
  get<MetricReport>(
    `/metric-reports/${platformId}/${version}/${metricName}?baseline=previous`,
  );
